'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { CircleX } from 'lucide-react'

import { PlayButton } from '@/blocks/VideoEmbed/PlayButton'
import { VideoWatchLink } from '@/components/VideoWatchLink'
import { cn } from '@/utilities/ui'
import {
  YOUTUBE_PLACEHOLDER,
  youtubeThumbnailChain,
  youtubeThumbnailUrl,
} from '@/utilities/videoThumbnail'

type Props = {
  title: string
  embedUrl: string
  provider: 'youtube' | 'vimeo'
  videoId: string
  /** From the Videos record. Required for Vimeo, optional for YouTube. */
  customThumbnailUrl: string | null
  /** Portrait source, so the thumbnail chain starts from the uncropped endpoint. */
  isShort: boolean
  watchUrl: string | null
}

const EMBED_ORIGIN = 'https://www.youtube-nocookie.com'

// Smallest of: the design width, the viewport minus page gutters, and the width that keeps a
// 16/9-tall player inside the viewport with 160px spare for margins and the close button.
// 0.5625 is 9/16 as a decimal, since the height term is a width.
const PLAYER_SIZE = 'w-[min(405px,calc(100vw-56px),calc((100dvh-160px)*0.5625))] aspect-[9/16]'

export const VerticalVideoPlayer: React.FC<Props> = ({
  title,
  embedUrl,
  provider,
  videoId,
  customThumbnailUrl,
  isShort,
  watchUrl,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isIframeLoading, setIsIframeLoading] = useState(true)
  const [thumbnailFallbackIndex, setThumbnailFallbackIndex] = useState(0)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  const playButtonRef = useRef<HTMLButtonElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const hasPreconnected = useRef(false)

  const chain = youtubeThumbnailChain(isShort)
  const thumbnailUrl =
    customThumbnailUrl ??
    (provider === 'youtube'
      ? youtubeThumbnailUrl(videoId, chain[thumbnailFallbackIndex] ?? chain[chain.length - 1]!)
      : null)

  const nextThumbnail = useCallback(() => {
    if (customThumbnailUrl || provider !== 'youtube') return
    setThumbnailFallbackIndex((index) => (index < chain.length - 1 ? index + 1 : index))
  }, [customThumbnailUrl, provider, chain.length])

  // A "successful" load at the placeholder's dimensions is really a 404 (see YOUTUBE_PLACEHOLDER).
  const handleThumbnailLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget
      if (
        naturalWidth === YOUTUBE_PLACEHOLDER.width &&
        naturalHeight === YOUTUBE_PLACEHOLDER.height
      ) {
        nextThumbnail()
      }
    },
    [nextThumbnail],
  )

  // Warm the embed handshake on hover/focus so the click doesn't pay for DNS + TLS.
  const preconnect = useCallback(() => {
    if (hasPreconnected.current) return
    hasPreconnected.current = true
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = EMBED_ORIGIN
    link.crossOrigin = ''
    document.head.appendChild(link)
  }, [])

  const open = useCallback(() => {
    setIsIframeLoading(true)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    const trigger = playButtonRef.current
    if (trigger) window.requestAnimationFrame(() => trigger.focus())
  }, [])

  // Mount the portal target after hydration so SSR renders the facade only.
  useEffect(() => {
    setPortalTarget(document.body)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const dialog = dialogRef.current
    const inertedSiblings: HTMLElement[] = []
    if (dialog) {
      Array.from(document.body.children).forEach((el) => {
        if (el === dialog || el.contains(dialog)) return
        const htmlEl = el as HTMLElement
        if (!htmlEl.inert) {
          htmlEl.inert = true
          inertedSiblings.push(htmlEl)
        }
      })
    }

    return () => {
      document.body.style.overflow = previousOverflow
      inertedSiblings.forEach((el) => {
        el.inert = false
      })
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    closeButtonRef.current?.focus()
  }, [isOpen])

  // shift+Tab is swallowed so focus can't reach the inert page behind.
  useEffect(() => {
    if (!isOpen) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key === 'Tab' && event.shiftKey && document.activeElement === closeButtonRef.current) {
        event.preventDefault()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, close])

  if (!thumbnailUrl) return null

  const dialog = isOpen && (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Video: ${title}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) close()
      }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-civo-black/90"
    >
      <div className={cn('relative', PLAYER_SIZE)}>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={close}
          aria-label="Close video"
          // p-[7px] pads the 30px glyph out to a 44px touch target.
          className="absolute -top-[49px] right-[-7px] cursor-pointer rounded-full p-[7px] text-civo-white transition-colors duration-200 hover:text-civo-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-white motion-reduce:transition-none"
        >
          <CircleX className="size-[30px]" strokeWidth={1.5} aria-hidden="true" />
        </button>

        <div className="relative size-full overflow-hidden rounded-[20px] bg-civo-black">
          {isIframeLoading && (
            <div aria-hidden="true" className="absolute inset-0 z-[1] bg-civo-black">
              <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent motion-reduce:animate-none" />
            </div>
          )}
          <iframe
            src={`${embedUrl}?autoplay=1&playsinline=1&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-presentation"
            onLoad={() => setIsIframeLoading(false)}
            className="absolute inset-0 size-full"
          />
        </div>
        {watchUrl && (
          <VideoWatchLink
            href={watchUrl}
            provider={provider}
            title={title}
            className="absolute left-1/2 top-full -translate-x-1/2 text-civo-white"
          />
        )}
      </div>

      {/* Tab out of the iframe lands here and bounces back, closing the focus loop. */}
      <span tabIndex={0} onFocus={() => closeButtonRef.current?.focus()} />
    </div>
  )

  return (
    <>
      <div
        className={cn(
          'relative w-full shrink-0 overflow-hidden rounded-[20px]',
          'aspect-[255/320] md:aspect-[600/459] lg:aspect-[373/459] lg:w-[373px]',
        )}
      >
        <Image
          src={thumbnailUrl}
          alt={title}
          fill
          className="object-cover"
          // Card padding + page gutters: 96 + 72 at md, 64 + 56 below it.
          sizes="(min-width: 1024px) 373px, (min-width: 768px) calc(100vw - 168px), calc(100vw - 120px)"
          unoptimized={thumbnailUrl.startsWith('https://img.youtube.com')}
          onLoad={handleThumbnailLoad}
          onError={nextThumbnail}
        />
        <div aria-hidden="true" className="absolute inset-0 z-[1] bg-black/30" />
        <PlayButton
          ref={playButtonRef}
          onClick={open}
          onPointerEnter={preconnect}
          onFocus={preconnect}
          label={`Play video: ${title}`}
          aria-haspopup="dialog"
          className="size-14 md:size-[72px] lg:size-24"
          iconClassName="w-[15px] h-4 md:w-[19.5px] md:h-[21px] lg:w-[26px] lg:h-7"
        />
      </div>

      {portalTarget && dialog && createPortal(dialog, portalTarget)}
    </>
  )
}
